import _ from 'lodash';
import React from 'react';

import { styleVars } from 'gooey';
import { cn } from '#/utils/classnames';

export interface ImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /**
   * The source of the image, in relation to the images folder (/public/assets/images).
   *
   * @example
   *  src='features/docs/hero.png'
   */
  src: string;
  /**
   * The alt text for the image.
   */
  alt: string;
  /**
   * The location of the image.
   * @default 'public'
   */
  location?: 'public' | 'static';
  /**
   * Used to set the ratio of the image.
   * @default 300
   */
  height: number;
  /**
   * Used to set the ratio of the image.
   * @default 200
   */
  width: number;
  /**
   * Should the image be responsive?
   * @default false
   */
  responsive?: boolean;
  /**
   * If the image should be lazy loaded.
   * @default 'lazy'
   */
  loading?: 'lazy' | 'eager';
}

const Img = React.forwardRef<HTMLImageElement, ImgProps>(
  (
    {
      className,
      responsive = false,
      src,
      height = 200,
      width = 300,
      location = 'public',
      loading = 'lazy',
      style,
      alt,
      ...restProps
    },
    ref
  ) => {
    const [error, setError] = React.useState(false);

    const formattedSrc = React.useMemo(() => {
      return `${location === 'static' ? '' : '/images'}/${_.trimStart(src, '/')}`;
    }, [src, location]);

    if (error) {
      return (
        <div
          className={cn(
            'h-auto w-full rounded bg-gray-900 flex flex-col items-center justify-center',
            responsive ? 'max-w-full' : 'w-(--quark-image-width)',
            className
          )}
          style={styleVars({
            '--quark-image-width': `${width}px`,
          })}
        >
          <div className='text-gray-400'>oops</div>
        </div>
      );
    }

    return (
      <img
        {...restProps}
        alt={alt}
        loading={loading}
        src={formattedSrc}
        ref={ref}
        height={height}
        width={width}
        onLoad={() => {
          setError(false);
        }}
        onError={() => {
          setError(true);
        }}
        className={cn(
          'quarks-image',
          responsive ? 'h-auto w-full' : 'h-auto w-(--quark-image-width)',
          className
        )}
        style={{
          ...styleVars({
            '--quark-image-width': `${width}px`,
          }),
          ...style,
        }}
      />
    );
  }
);
Img.displayName = 'Img';

export { Img };
